import { use } from 'react';
import { useUrl, useParams } from 'crossroad';
import { Button } from '@/components/ui/button';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { TranslationDataContext } from '@/data/translation/translation-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { findPassageForParagraph } from './find-passage-for-paragraph';
import { parseRef } from '@/data/passage/passage-data-service';
import { getParagraphsFromPassage } from '@/data/passage/passage-utils';
import { findParagraphIndexForVerse } from '@/lib/utils/scroll-position';
import type { BibleBook } from '@/lib/utils/bible-utils';
import { useHydrationStage } from '@/lib/hooks/use-hydration-stage';
import { DigModeIcon } from '@/components/ui/icons/dig-mode-icon';

export function StudyModeToggle({ id }: { id?: string }) {
    const { isRendering } = useHydrationStage();
    const [, setUrl] = useUrl();
    const { scrollState, passageId } = usePassageState();
    const { data: paragraphsData } = use(TranslationDataContext);
    const { data: passageData, get: getPassage } = use(PassageDataContext);

    const passageIdFromPassageUrl = useParams('/app/passage/:id')?.id;
    const passageIdFromContextUrl = useParams('/app/passage/:id/context/:contextCategory')?.id;
    const passageIdFromUrl = passageIdFromPassageUrl || passageIdFromContextUrl || null;
    const isActive = !!passageId; // Check if we're currently on a passage details page

    function handleToggle() {
        if (isActive) {
            // Exit study mode - go back to reading view
            // Always use the current passage's book/chapter (not scrollState)
            if (!passageIdFromUrl || !passageData || !paragraphsData) {
                console.error('Cannot exit study mode: no passage ID or data available');
                return;
            }

            const currentPassage = getPassage(passageIdFromUrl);
            if (!currentPassage) {
                console.error('Cannot exit study mode: passage not found', passageIdFromUrl);
                return;
            }

            const parsedRef = parseRef(currentPassage.ref);
            if (!parsedRef) {
                console.error('Cannot exit study mode: invalid passage reference', currentPassage.ref);
                return;
            }

            const book = currentPassage.book as BibleBook;
            const chapter = parsedRef.chapterStart;

            // Find the scroll position of the first paragraph in this passage
            const translation = paragraphsData.items[0];
            if (translation?.paragraphs) {
                // Fast path: Use pre-calculated paragraph range if available
                const paragraphRange = currentPassage.paragraphRange?.[translation.version];
                if (paragraphRange) {
                    // Get the first paragraph to extract verse
                    const firstParagraph = translation.paragraphs[paragraphRange.startIndex];
                    if (firstParagraph) {
                        const parts = firstParagraph.r.split(':');
                        if (parts.length === 5) {
                            const verse = parseInt(parts[2], 10); // verseStart
                            setUrl(`/app/read/${book}/${chapter}?verse=${verse}`);
                            return;
                        }
                    }
                }

                // Fallback: Calculate paragraphs and find index
                const passageParagraphs = getParagraphsFromPassage(
                    translation.paragraphs,
                    currentPassage,
                    translation.version,
                    false,
                );

                if (passageParagraphs.length > 0) {
                    // Extract verse from first paragraph
                    const firstParagraph = passageParagraphs[0];
                    const parts = firstParagraph.r.split(':');
                    if (parts.length === 5) {
                        const verse = parseInt(parts[2], 10); // verseStart
                        setUrl(`/app/read/${book}/${chapter}?verse=${verse}`);
                        return;
                    }
                }
            }

            // Fallback: navigate without verse
            setUrl(`/app/read/${book}/${chapter}`);
        }
        else {
            // Enter dig mode - find and navigate to passage details
            if (!paragraphsData || !passageData || !scrollState.book || !scrollState.chapter || scrollState.verse === null) {
                console.error('Cannot enter dig mode: missing data', { paragraphsData: !!paragraphsData, passageData: !!passageData, scrollState });
                return;
            }

            // Find paragraph index from verse
            const translation = paragraphsData.items[0];
            if (!translation || !translation.paragraphs || !translation.startPositions) {
                console.error('Cannot enter dig mode: translation data incomplete');
                return;
            }
            const paragraphIndex = findParagraphIndexForVerse(
                translation.paragraphs,
                translation.startPositions,
                scrollState.book,
                scrollState.chapter,
                scrollState.verse
            );

            const paragraph = translation.paragraphs[paragraphIndex];
            if (!paragraph) {
                console.error('No paragraph found for verse', scrollState);
                return;
            }

            const passageId = findPassageForParagraph(paragraph, passageData.items);
            if (!passageId) {
                console.error('No passage found for current scroll position', scrollState);
                return;
            }

            // Navigate to passage details WITHOUT query parameters
            setUrl(`/app/passage/${passageId}`);
        }
    }

    if (!isRendering) {
        return null; // Hide toggle until text data is ready
    }

    return (
        <Button
            id={id}
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            className={isActive ? 'bg-accent text-accent-foreground' : ''}
            title={isActive ? 'Exit Dig Mode' : 'Enter Dig Mode'}
        >
            <DigModeIcon isActive={isActive} className="size-[22px]" />
        </Button>
    );
}

